import type { operations } from "@/openapi.gen.ts";
import { AGENT_SCHEDULES_QUERY_PREFIX } from "@/services/agent-schedules-service.ts";
import { $api } from "@/utils/fetch.ts";
import { type QueryKey, useQueryClient } from "@tanstack/react-query";

/**
 * A trigger of either kind as returned by the triggers endpoints. For a cron
 * trigger `nextRunAt` is computed server-side (it matches the worker's next
 * fire) and is the source of truth for the "next run" preview — the client
 * never recomputes it from cron.
 *
 * Derived from the generated API rather than hand-written, so a change to the
 * endpoint's shape breaks the type here instead of at runtime.
 */
export type AgentTrigger =
	operations["api_agent-definitions_agentId_triggers_get"]["responses"][200]["content"]["application/json"]["triggers"][number];

/**
 * A cron trigger, with the fields only that kind carries. Every trigger that is
 * not one is an event trigger: {@link isScheduleTrigger} is the one
 * discriminator the surfaces split on, for an agent's triggers and for the
 * org-wide dashboard rows alike.
 */
export type ScheduleTrigger = AgentTrigger & {
	kind: "cron";
	cron: string;
	timezone: string;
	nextRunAt: string;
};

export const isScheduleTrigger = <
	T extends Pick<AgentTrigger, "kind" | "cron" | "timezone" | "nextRunAt">,
>(
	trigger: T,
): trigger is T &
	Pick<ScheduleTrigger, "kind" | "cron" | "timezone" | "nextRunAt"> =>
	trigger.kind === "cron" &&
	trigger.cron !== undefined &&
	trigger.timezone !== undefined &&
	trigger.nextRunAt !== undefined;

/**
 * The body create and update both take (PUT replaces the whole trigger). A cron
 * body omits `kind`, which the API reads as cron.
 */
export type AgentTriggerBody =
	operations["api_agent-definitions_agentId_triggers_post"]["requestBody"]["content"]["application/json"];
export type EventTriggerBody = Extract<AgentTriggerBody, { kind: "event" }>;

/**
 * An agent's triggers, both kinds. Unpaginated by design — the backend caps an
 * agent at a small fixed number of triggers, so there is no unbounded list to
 * page (the runs list pages only because run history is unbounded). Polls so a
 * `nextRunAt` / `lastRunAt` advanced by the scheduler worker surfaces without a
 * manual refresh, mirroring the runs surfaces.
 */
const TRIGGER_POLL_INTERVAL_MS = 30000;

export const agentTriggersQueryOptions = (agentId: string) =>
	$api.queryOptions(
		"get",
		"/api/agent-definitions/{agentId}/triggers",
		{ params: { path: { agentId } } },
		{ refetchInterval: TRIGGER_POLL_INTERVAL_MS },
	);

// openapi-react-query keys every query as [method, literalPath, init]. This
// prefix matches the triggers list so create / update / delete invalidations
// refetch it.
const triggersQueryPrefix = (agentId: string) => [
	"get",
	"/api/agent-definitions/{agentId}/triggers",
	{ params: { path: { agentId } } },
];

/**
 * Every query a trigger write leaves stale: this agent's triggers list, and the
 * org-wide schedules panel — a different literal path, so no prefix of the
 * triggers key can reach it (TanStack's `partialMatchKey` compares element 2 by
 * exact string).
 */
export const agentTriggerInvalidationKeys = (agentId: string): QueryKey[] => [
	triggersQueryPrefix(agentId),
	AGENT_SCHEDULES_QUERY_PREFIX,
];

export const useCreateAgentTriggerMutation = (agentId: string) => {
	const queryClient = useQueryClient();
	return $api.useMutation("post", "/api/agent-definitions/{agentId}/triggers", {
		onSuccess: () => {
			for (const queryKey of agentTriggerInvalidationKeys(agentId)) {
				queryClient.invalidateQueries({ queryKey });
			}
		},
	});
};

export const useUpdateAgentTriggerMutation = (agentId: string) => {
	const queryClient = useQueryClient();
	return $api.useMutation(
		"put",
		"/api/agent-definitions/{agentId}/triggers/{triggerId}",
		{
			onSuccess: () => {
				for (const queryKey of agentTriggerInvalidationKeys(agentId)) {
					queryClient.invalidateQueries({ queryKey });
				}
			},
		},
	);
};

export const useDeleteAgentTriggerMutation = (agentId: string) => {
	const queryClient = useQueryClient();
	return $api.useMutation(
		"delete",
		"/api/agent-definitions/{agentId}/triggers/{triggerId}",
		{
			onSuccess: () => {
				for (const queryKey of agentTriggerInvalidationKeys(agentId)) {
					queryClient.invalidateQueries({ queryKey });
				}
			},
		},
	);
};
